import type {CourseDocument} from '@cip/core/firebase/client';
import {Role} from 'types/role';

/** The course-document fields that announce application decisions. */
export type DecisionAnnouncementFields = Pick<
  CourseDocument,
  'studentAppDecisionsLive' | 'slAppDecisionsLive'
>;

/** Roles granted by admitting a student applicant. */
const STUDENT_ADMISSION_ROLES: ReadonlySet<Role> = new Set([
  Role.STUDENT,
  Role.EXPERIENCED_STUDENT,
]);

/** Roles granted by admitting a section leader applicant. */
const SL_ADMISSION_ROLES: ReadonlySet<Role> = new Set([
  Role.SECTION_LEADER,
  Role.SENIOR_SL,
  Role.MENTOR,
]);

/**
 * The course role a user is shown, given whether the course has announced the
 * admission decision behind it.
 *
 * A student-track role (student, experienced student) is withheld while
 * `studentAppDecisionsLive` is stored as `false`, and a section-leader-track
 * role (section leader, senior section leader, mentor) while
 * `slAppDecisionsLive` is. A withheld role reads as unregistered, so the user
 * sees the course exactly as an applicant without a role would. Only an
 * explicit `false` withholds: an absent flag means the course has no
 * announcement step, and its roles show as soon as they are written. Staff
 * roles are never withheld.
 *
 * @param params.role - The user's stored course role. Omitted → the user has
 *   none, and undefined is returned.
 * @param params.courseData - The course document. Omitted → nothing is
 *   withheld, so callers must wait for the document before deciding.
 * @return `role`, or `Role.UNREGISTERED` when it is withheld.
 * @throws Never.
 */
export function getAnnouncedRole({
  role,
  courseData,
}: {
  role?: Role;
  courseData?: DecisionAnnouncementFields;
}): Role | undefined {
  if (role === undefined || !courseData) {
    return role;
  }
  if (
    STUDENT_ADMISSION_ROLES.has(role) &&
    courseData.studentAppDecisionsLive === false
  ) {
    return Role.UNREGISTERED;
  }
  if (
    SL_ADMISSION_ROLES.has(role) &&
    courseData.slAppDecisionsLive === false
  ) {
    return Role.UNREGISTERED;
  }
  return role;
}


/**
 * Whether a role is one that admitting an applicant on the given track
 * grants: student or experienced student for the student application, and
 * section leader, senior section leader or mentor for the section leader
 * application.
 *
 * @param params.role - The user's stored course role. Omitted → false.
 * @param params.track - Which application the role is checked against.
 * @return True when `role` is one of the track's admission roles.
 * @throws Never.
 */
export function isAdmissionRole({
  role,
  track,
}: {
  role?: Role;
  track: 'student' | 'sl';
}): boolean {
  if (role === undefined) {
    return false;
  }
  return track === 'sl'
    ? SL_ADMISSION_ROLES.has(role)
    : STUDENT_ADMISSION_ROLES.has(role);
}
